import React, { useState, useCallback } from "react";
import Quick4Review from "./Quick4Review";
import FlashcardReview from "./FlashcardReview";
import FlashcardStatistics from "./FlashcardStatistics";
import FlashcardOptions from "./FlashcardOptions";
import VocabularySelectionModal from "./VocabularySelectionModal";

const FlashcardModal = React.memo(({
  showFlashcardModal,
  setShowFlashcardModal,
  flashcardModalStep,
  setFlashcardModalStep,
  vocabularyList,
  getDueWords,
  getReviewPriority,
  onUpdateVocabulary,
  playVocabAudio,
  playSentenceAudio,
  stopAllAudio,
  playGoogleTTS,
  calculateNextReview
}) => {
  // Quick 4 Review states
  const [isQuick4Review, setIsQuick4Review] = useState(false);
  const [quick4ReviewStep, setQuick4ReviewStep] = useState("preview");
  const [quick4ReviewWords, setQuick4ReviewWords] = useState([]);
  const [quick4CurrentIndex, setQuick4CurrentIndex] = useState(0);
  const [quick4Results, setQuick4Results] = useState([]);
  const [quick4SpellingInput, setQuick4SpellingInput] = useState("");
  const [quick4SpellingResult, setQuick4SpellingResult] = useState(null);
  const [quick4DifficultWords, setQuick4DifficultWords] = useState([]);
  const [quick4DifficultIndex, setQuick4DifficultIndex] = useState(0);
  const [quick4DifficultDirection, setQuick4DifficultDirection] = useState("jp-vi");
  const [showVietnameseMeaning, setShowVietnameseMeaning] = useState(false);

  // Word selection states
  const [selectedWords, setSelectedWords] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Regular flashcard states
  const [reviewQueue, setReviewQueue] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [flashcardFront, setFlashcardFront] = useState("jp");
  const [showSpellingTest, setShowSpellingTest] = useState(false);
  const [spellingInput, setSpellingInput] = useState("");
  const [spellingResult, setSpellingResult] = useState(null);

  const resetQuick4Review = useCallback(() => {
    setIsQuick4Review(false);
    setQuick4ReviewStep("preview");
    setQuick4ReviewWords([]);
    setQuick4CurrentIndex(0);
    setQuick4Results([]);
    setQuick4SpellingInput("");
    setQuick4SpellingResult(null);
    setQuick4DifficultWords([]);
    setQuick4DifficultIndex(0);
    setQuick4DifficultDirection("jp-vi");
    setShowVietnameseMeaning(false);
    stopAllAudio();
  }, [stopAllAudio]);

  const startQuick4Review = useCallback(() => {
    const dueWords = getDueWords();
    const urgentWords = vocabularyList.filter((word) => getReviewPriority(word) === "urgent");
    const warningWords = vocabularyList.filter((word) => getReviewPriority(word) === "warning");

    // Combine urgent and warning words for priority
    const priorityWords = [...urgentWords, ...warningWords, ...dueWords];

    // Remove duplicates and get unique words
    const uniqueWords = priorityWords.filter(
      (word, index, self) => index === self.findIndex((w) => w.id === word.id)
    );

    if (uniqueWords.length === 0) {
      alert("Không có từ vựng khẩn cấp hoặc cần ôn tập!");
      return;
    }

    // Randomly select 4 words (or all if less than 4)
    const shuffled = [...uniqueWords].sort(() => 0.5 - Math.random());
    const selectedWords = shuffled.slice(0, Math.min(4, shuffled.length));

    // Initialize review session
    setQuick4ReviewWords(selectedWords);
    setQuick4Results(selectedWords.map(() => ({ jpVi: null, viJp: null })));
    setQuick4ReviewStep("preview");
    setQuick4CurrentIndex(0);
    setQuick4SpellingInput("");
    setQuick4SpellingResult(null);
    setQuick4DifficultWords([]);
    setQuick4DifficultIndex(0);
    setQuick4DifficultDirection("jp-vi");
    setShowVietnameseMeaning(false);
    setIsQuick4Review(true);
    setFlashcardModalStep("quick4review");

    stopAllAudio();
  }, [vocabularyList, getDueWords, getReviewPriority, stopAllAudio, setFlashcardModalStep]);

  const startCustomReview = useCallback(() => {
    const wordsArray = Array.from(selectedWords);
    const wordsToReview = vocabularyList.filter(word => wordsArray.includes(word.id));
    
    if (wordsToReview.length === 0) {
      alert("Vui lòng chọn ít nhất một từ vựng để kiểm tra!");
      return;
    }

    // Initialize review session with selected words
    setQuick4ReviewWords(wordsToReview);
    setQuick4Results(wordsToReview.map(() => ({ jpVi: null, viJp: null })));
    setQuick4ReviewStep("preview");
    setQuick4CurrentIndex(0);
    setQuick4SpellingInput("");
    setQuick4SpellingResult(null);
    setQuick4DifficultWords([]);
    setQuick4DifficultIndex(0);
    setQuick4DifficultDirection("jp-vi");
    setShowVietnameseMeaning(false);
    setIsQuick4Review(true);
    setFlashcardModalStep("quick4review");

    stopAllAudio();
  }, [selectedWords, vocabularyList, stopAllAudio, setFlashcardModalStep]);

  const toggleWordSelection = useCallback((wordId) => {
    setSelectedWords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(wordId)) {
        newSet.delete(wordId);
      } else {
        newSet.add(wordId);
      }
      return newSet;
    });
  }, []);

  const getFilteredWordsForSelection = useCallback(() => {
    if (!searchQuery.trim()) return vocabularyList;
    
    const query = searchQuery.toLowerCase().trim();
    return vocabularyList.filter((word) =>
      word.wordJp.toLowerCase().includes(query) ||
      word.wordVi.toLowerCase().includes(query) ||
      (word.exampleSentence && word.exampleSentence.toLowerCase().includes(query))
    );
  }, [vocabularyList, searchQuery]);

  const selectAllWords = useCallback(() => {
    const filteredWords = getFilteredWordsForSelection();
    const allWordIds = filteredWords.map(word => word.id);
    setSelectedWords(new Set(allWordIds));
  }, [getFilteredWordsForSelection]);

  const clearAllSelection = useCallback(() => {
    setSelectedWords(new Set());
  }, []);

  const closeModal = useCallback(() => {
    stopAllAudio();
    setShowFlashcardModal(false);
    setFlashcardModalStep("options");
    setReviewQueue([]);
    setCurrentCardIndex(0);
    setShowAnswer(false);
    setShowSpellingTest(false);
    setSpellingInput("");
    setSpellingResult(null);
    setSelectedWords(new Set());
    setSearchQuery("");
    resetQuick4Review();
  }, [stopAllAudio, setShowFlashcardModal, setFlashcardModalStep, resetQuick4Review]);

  if (!showFlashcardModal) return null;

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-start justify-center pt-20 z-50 p-4 animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-black/95 rounded-2xl border border-purple-400/50 shadow-neon-purple backdrop-blur-sm p-6 max-w-6xl w-full animate-slideUp relative max-h-[89vh] overflow-y-auto">
        
        {/* Options Step */}
        {flashcardModalStep === "options" && (
          <>
            <FlashcardStatistics
              vocabularyList={vocabularyList}
              getDueWords={getDueWords}
              getReviewPriority={getReviewPriority}
            />
            <FlashcardOptions
              vocabularyList={vocabularyList}
              getDueWords={getDueWords}
              getReviewPriority={getReviewPriority}
              startQuick4Review={startQuick4Review}
              setFlashcardModalStep={setFlashcardModalStep}
            />
          </>
        )}

        {/* Word Selection Step */}
        {flashcardModalStep === "select" && (
          <VocabularySelectionModal
            vocabularyList={vocabularyList}
            selectedWords={selectedWords}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            toggleWordSelection={toggleWordSelection}
            selectAllWords={selectAllWords}
            clearAllSelection={clearAllSelection}
            getFilteredWordsForSelection={getFilteredWordsForSelection}
            startCustomReview={startCustomReview}
            setFlashcardModalStep={setFlashcardModalStep}
          />
        )}

        {/* Quick 4 Review */}
        {flashcardModalStep === "quick4review" && (
          <Quick4Review
            quick4ReviewStep={quick4ReviewStep}
            setQuick4ReviewStep={setQuick4ReviewStep}
            quick4ReviewWords={quick4ReviewWords}
            quick4CurrentIndex={quick4CurrentIndex}
            setQuick4CurrentIndex={setQuick4CurrentIndex}
            quick4Results={quick4Results}
            setQuick4Results={setQuick4Results}
            quick4SpellingInput={quick4SpellingInput}
            setQuick4SpellingInput={setQuick4SpellingInput}
            quick4SpellingResult={quick4SpellingResult}
            setQuick4SpellingResult={setQuick4SpellingResult}
            quick4DifficultWords={quick4DifficultWords}
            setQuick4DifficultWords={setQuick4DifficultWords}
            quick4DifficultIndex={quick4DifficultIndex}
            setQuick4DifficultIndex={setQuick4DifficultIndex}
            quick4DifficultDirection={quick4DifficultDirection}
            setQuick4DifficultDirection={setQuick4DifficultDirection}
            showVietnameseMeaning={showVietnameseMeaning}
            setShowVietnameseMeaning={setShowVietnameseMeaning}
            playVocabAudio={playVocabAudio}
            playSentenceAudio={playSentenceAudio}
            playGoogleTTS={playGoogleTTS}
            stopAllAudio={stopAllAudio}
            calculateNextReview={calculateNextReview}
            onUpdateVocabulary={onUpdateVocabulary}
            resetQuick4Review={resetQuick4Review}
            setFlashcardModalStep={setFlashcardModalStep}
            startQuick4Review={startQuick4Review}
          />
        )}

        {/* Regular Flashcard Review */}
        {flashcardModalStep === "review" && (
          <FlashcardReview
            reviewQueue={reviewQueue}
            setReviewQueue={setReviewQueue}
            currentCardIndex={currentCardIndex}
            setCurrentCardIndex={setCurrentCardIndex}
            showAnswer={showAnswer}
            setShowAnswer={setShowAnswer}
            flashcardFront={flashcardFront}
            setFlashcardFront={setFlashcardFront}
            showSpellingTest={showSpellingTest}
            setShowSpellingTest={setShowSpellingTest}
            spellingInput={spellingInput}
            setSpellingInput={setSpellingInput}
            spellingResult={spellingResult}
            setSpellingResult={setSpellingResult}
            playVocabAudio={playVocabAudio}
            playSentenceAudio={playSentenceAudio}
            playGoogleTTS={playGoogleTTS}
            stopAllAudio={stopAllAudio}
            calculateNextReview={calculateNextReview}
            onUpdateVocabulary={onUpdateVocabulary}
            setFlashcardModalStep={setFlashcardModalStep}
          />
        )}

        {/* Close Button */}
        <button
          onClick={closeModal}
          className="group absolute top-3 right-3 p-2 bg-black/80 border border-red-400/50 text-red-400 hover:text-red-100 hover:border-red-400 rounded-xl transition-all duration-300 hover:shadow-neon-pink backdrop-blur-sm font-mono"
          title="ĐÓNG HỆ THỐNG"
        >
          <svg
            className="w-5 h-5 transform group-hover:rotate-90 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          <div className="absolute inset-0 border border-red-400/30 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 animate-border-pulse"></div>
        </button>
      </div>
    </div>
  );
});

export default FlashcardModal; 